import React from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { pageWorkspace } from "@/lib/operationalContext";
import NotificationBell from "@/components/NotificationBell";
import SyncStatusPill from "@/components/SyncStatusPill";
import BackOfficeSyncPill from "@/components/BackOfficeSyncPill";
import { OperationalBoundary } from "@/components/OperationalBoundary";
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Receipt,
  Factory,
  Truck,
  BarChart3,
  Sparkles,
  LogOut,
  TrendingUp,
  TrendingDown,
  Carrot,
  ClipboardCheck,
  CalendarRange,
  Wallet,
  Library,
  Menu,
  X,
  Sun,
  Moon,
  ListOrdered,
  ChevronDown,
  CreditCard,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/context/PermissionsContext";
import { useOutlet } from "@/context/OutletContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Store, Tag, Clock, ChefHat, Building2, Utensils, Sunrise, Sunset, Shield, Radio, HandCoins, Layers, Ban, Calculator, PiggyBank, ShieldCheck, GitCompareArrows, FileText, LineChart, LayoutGrid, Warehouse, Wrench, ChevronLeft, ChevronRight, Printer, Settings, Download, FlaskConical } from "lucide-react";

// ERPNext-style grouped navigation
const SECTIONS = [
  {
    title: "Home",
    links: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", roles: ["admin", "manager", "staff", "central_kitchen"] },
      { to: "/owner-control", label: "Owner Control Tower", icon: BarChart3, testid: "nav-owner-control", roles: ["admin"] },
      { to: "/outlet-workspace", label: "Outlet Workspace", icon: Store, testid: "nav-outlet-workspace", roles: ["admin", "manager", "outlet_manager", "staff", "cashier", "outlet"] },
      { to: "/area-workspace", label: "Area Workspace", icon: Store, testid: "nav-area-workspace", roles: ["admin", "manager", "area_manager"] },
    ],
  },
  {
    title: "Stock",
    links: [
      { to: "/inventory", label: "Item Master", icon: Package, testid: "nav-inventory", roles: ["admin", "manager", "staff", "central_kitchen"] },
      { to: "/operational-catalogue", label: "Catalogue & Workspaces", icon: Layers, testid: "nav-operational-catalogue", roles: ["admin"] },
      { to: "/stock-value", label: "Stock & Stock Value", icon: Calculator, testid: "nav-stock-value", roles: ["admin", "manager", "central_kitchen"] },
      { to: "/cycle-count", label: "Stock Reconciliation", icon: ClipboardCheck, testid: "nav-cycle-count", roles: ["admin", "manager"] },
      { to: "/lot-integrity", label: "Lot vs Stock Check", icon: Calculator, testid: "nav-lot-integrity", roles: ["admin"] },
      { to: "/supply-chain", label: "Stock Transfer", icon: Truck, testid: "nav-supply-chain", roles: ["admin", "manager", "staff", "central_kitchen"] },
      { to: "/stock-entries", label: "Stock Entries", icon: GitCompareArrows, testid: "nav-stock-entries", roles: ["admin", "manager", "central_kitchen"] },
      { to: "/stock-ledger", label: "Stock Ledger", icon: Layers, testid: "nav-stock-ledger", roles: ["admin", "manager", "central_kitchen"] },
      { to: "/stock-ageing", label: "Stock Ageing", icon: Clock, testid: "nav-stock-ageing", roles: ["admin", "manager", "central_kitchen"] },
    ],
  },
  {
    title: "Manufacturing",
    links: [
      { to: "/production", label: "Recipes / BOM", icon: Factory, testid: "nav-production", roles: ["admin", "manager", "central_kitchen"] },
      { to: "/work-orders", label: "Work Orders", icon: Wrench, testid: "nav-work-orders", roles: ["admin", "manager", "central_kitchen"] },
      { to: "/kds", label: "Kitchen Display (KDS)", icon: ChefHat, testid: "nav-kds", roles: ["admin", "central_kitchen"] },
      { to: "/warehouses", label: "Warehouses", icon: Warehouse, testid: "nav-warehouses", roles: ["admin", "manager"] },
      { to: "/monthly-planning", label: "Production Planning", icon: CalendarRange, testid: "nav-monthly-planning", roles: ["admin", "manager", "central_kitchen"] },
      { to: "/production-period", label: "Period & Stage Planning", icon: Layers, testid: "nav-production-period", roles: ["admin", "manager", "central_kitchen"] },
      { to: "/day-end-production", label: "Day-End Production", icon: CalendarRange, testid: "nav-day-end", roles: ["admin", "manager", "central_kitchen"] },
      { to: "/daily-closing", label: "Daily Closing", icon: Sunset, testid: "nav-daily-closing", roles: ["admin", "manager"] },
      { to: "/outlet-menu-editor", label: "Outlet Menu Editor", icon: Store, testid: "nav-outlet-menu-editor", roles: ["admin"] },
      { to: "/prep-sheet", label: "Morning Prep Sheet", icon: Sunrise, testid: "nav-prep-sheet", roles: ["admin", "manager", "central_kitchen"] },
      { to: "/labels", label: "Batch Labels", icon: Tag, testid: "nav-labels", roles: ["admin", "manager", "central_kitchen"] },
      { to: "/nutrition", label: "Nutrition Management", icon: FlaskConical, testid: "nav-nutrition", roles: ["admin", "manager"] },
      { to: "/batches", label: "Batches & Expiry", icon: Clock, testid: "nav-batches", roles: ["admin", "manager", "central_kitchen"] },
    ],
  },
  {
    title: "Buying",
    links: [
      { to: "/central-kitchen", label: "Central Kitchen", icon: Warehouse, testid: "nav-central-kitchen", roles: ["admin", "manager", "central_kitchen"] },
      { to: "/procurement", label: "Material Request", icon: Carrot, testid: "nav-procurement", roles: ["admin", "manager", "central_kitchen"] },
      { to: "/auto-po", label: "Auto-PO (Reorder)", icon: ShoppingCart, testid: "nav-auto-po", roles: ["admin", "manager", "central_kitchen"] },
      { to: "/purchases", label: "Purchase Orders", icon: ShoppingCart, testid: "nav-purchases", roles: ["admin", "manager", "central_kitchen"] },
      { to: "/purchase-receipts", label: "Purchase Receipts", icon: FileText, testid: "nav-purchase-receipts", roles: ["admin", "manager", "central_kitchen"] },
      { to: "/vendor-payables", label: "Vendors & Payables", icon: CreditCard, testid: "nav-vendor-payables", roles: ["admin", "manager", "central_kitchen"] },
    ],
  },
  {
    title: "Selling",
    links: [
      { to: "/menu-catalog", label: "Menu & Consumption", icon: Utensils, testid: "nav-menu-catalog", roles: ["admin", "manager"] },
      { to: "/pos", label: "POS (Petpooja Tiles)", icon: ShoppingCart, testid: "nav-pos", roles: ["admin", "manager", "staff"] },
      { to: "/live-shifts", label: "Live Shifts", icon: Radio, testid: "nav-live-shifts", roles: ["admin", "manager"] },
      { to: "/all-orders", label: "All Orders", icon: Receipt, testid: "nav-all-orders", roles: ["admin", "manager"] },
      { to: "/setup", label: "Setup / Downloads", icon: Download, testid: "nav-setup", roles: ["admin", "manager", "staff", "central_kitchen"] },
      { to: "/users-permissions", label: "Users & Permissions", icon: ShieldCheck, testid: "nav-users-permissions", roles: ["admin", "manager"] },
      { to: "/print-groups", label: "Print Groups", icon: Printer, testid: "nav-print-groups", roles: ["admin", "manager"] },
      { to: "/printer-settings", label: "Printer Settings", icon: Settings, testid: "nav-printer-settings", roles: ["admin", "manager"] },
      { to: "/audit-log", label: "Audit Log", icon: Shield, testid: "nav-audit-log", roles: ["admin", "manager"] },
      { to: "/handover-history", label: "Handover History", icon: HandCoins, testid: "nav-handover-history", roles: ["admin", "manager"] },
      { to: "/menu-pdf", label: "Physical Menu PDF", icon: FileText, testid: "nav-menu-pdf", roles: ["admin", "manager"] },
      { to: "/86-board", label: "86 Board", icon: Ban, testid: "nav-86-board", roles: ["admin", "manager", "staff"] },
      { to: "/category-groups", label: "Category Groups", icon: Layers, testid: "nav-cat-groups", roles: ["admin", "manager"] },
      { to: "/sales", label: "Sales Orders", icon: Receipt, testid: "nav-sales", roles: ["admin", "manager", "staff", "central_kitchen"] },
      { to: "/delivery-notes", label: "Delivery Notes", icon: Truck, testid: "nav-delivery-notes", roles: ["admin", "manager", "central_kitchen", "outlet", "outlet_manager", "staff", "cashier"] },
      { to: "/tax-invoices", label: "Tax Invoices", icon: Receipt, testid: "nav-tax-invoices", roles: ["admin", "manager", "central_kitchen"] },
      { to: "/b2b-receivables", label: "B2B Customers & Receivables", icon: HandCoins, testid: "nav-b2b-receivables", roles: ["admin", "manager", "central_kitchen"] },
    ],
  },
  {
    title: "Reports",
    links: [
      { to: "/reports/sales", label: "Sales & Business", icon: BarChart3, testid: "nav-hub-sales", roles: ["admin", "manager"] },
      { to: "/reports/profitability", label: "Profitability", icon: Calculator, testid: "nav-hub-profit", roles: ["admin", "manager"] },
      { to: "/reports/consumption", label: "Consumption", icon: Utensils, testid: "nav-hub-consumption", roles: ["admin", "manager"] },
      { to: "/reports/production", label: "Production", icon: Factory, testid: "nav-hub-production", roles: ["admin", "manager", "central_kitchen"] },
      { to: "/reports/outlets", label: "Outlet Insights", icon: GitCompareArrows, testid: "nav-hub-outlets", roles: ["admin", "manager"] },
      { to: "/ai-insights", label: "Chef-AI", icon: Sparkles, testid: "nav-ai", roles: ["admin", "manager", "staff", "central_kitchen"] },
    ],
  },
  {
    title: "Settings",
    links: [
      { to: "/outlets", label: "Outlets & Users", icon: Store, testid: "nav-outlets", roles: ["admin", "manager"] },
      { to: "/permissions", label: "User Rights", icon: Shield, testid: "nav-permissions", roles: ["admin"] },
      { to: "/tax-templates", label: "GST Templates", icon: Calculator, testid: "nav-tax-templates", roles: ["admin", "manager"] },
      { to: "/settings", label: "Company Settings", icon: Building2, testid: "nav-settings", roles: ["admin", "manager"] },
      { to: "/backup-recovery", label: "Backup & Recovery", icon: ShieldCheck, testid: "nav-backup-recovery", roles: ["admin"] },
    ],
  },
];

// Simple Mode: only these paths stay in their own sections; everything else is
// grouped under a single collapsible "Advanced" section. Additive only — no
// routes removed, role-based filtering still applies on top of this.
const SIMPLE_PATHS = new Set([
  "/", "/owner-control", "/outlet-workspace", "/area-workspace", // Home
  "/pos", "/live-shifts",                                        // Selling
  "/central-kitchen", "/procurement", "/purchases",              // Buying
  "/production", "/work-orders",                                 // Manufacturing
  "/kds",                                                        // Kitchen Display
  "/reports/sales", "/reports/profitability", "/reports/consumption", // Reports
  "/reports/production", "/reports/outlets", "/ai-insights",
]);

// Rarely-used / power-user items. These are always pulled out of their native
// section into a single collapsible "Advanced" group (in BOTH Simple and Full
// mode) so the everyday menu stays short — Petpooja style. Routes are NOT
// removed; they stay reachable and keep their role/permission/context gating.
const ADVANCED_PATHS = new Set([
  "/owner-control", "/cost-dashboard", "/loaded-cost", "/margin-watchdog",
  "/86-board", "/menu-pdf", "/compare-outlets", "/monthly-expenses",
  "/traded-margin", "/consumption", "/consumption-variance", "/tax-templates",
  "/audit-log", "/backup-recovery", "/forecast", "/menu-ranks",
  "/business-reports", "/stock-ageing", "/stock-ledger", "/stock-entries",
  "/handover-history", "/production-period",
]);

export default function AppLayout({ children }) {
  const { pathname } = useLocation();
  const hardScope = pageWorkspace(pathname);
  const { user, logout } = useAuth();
  const { can } = usePermissions();
  const { outlets, current, setCurrent } = useOutlet();
  const navigate = useNavigate();
  const role = user?.role || "staff";
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(
    () => localStorage.getItem("ck-sidebar-collapsed") === "1"
  );
  const toggleCollapse = () => {
    setSidebarCollapsed((v) => {
      const next = !v;
      localStorage.setItem("ck-sidebar-collapsed", next ? "1" : "0");
      return next;
    });
  };
  const [theme, setTheme] = React.useState(localStorage.getItem("ck-theme") || "light");
  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    localStorage.setItem("ck-theme", next);
    document.documentElement.classList.toggle("dark", next === "dark");
  };
  // Simple Mode — fewer sidebar sections for daily use. Default ON for non-admin.
  const [simpleMode, setSimpleMode] = React.useState(() => {
    const saved = localStorage.getItem("ck-simple-mode");
    if (saved === "1") return true;
    if (saved === "0") return false;
    return role !== "admin";
  });
  const toggleSimpleMode = () =>
    setSimpleMode((v) => {
      const next = !v;
      localStorage.setItem("ck-simple-mode", next ? "1" : "0");
      return next;
    });

  // ---------- Petpooja-style context-aware sidebar ----------
  // When a specific outlet is picked in the top selector we prune the sidebar
  // to only what that context actually cares about. "All outlets" mode keeps
  // the full menu so admin can jump anywhere.
  const isCK = hardScope === "CENTRAL_KITCHEN" || (hardScope !== "OUTLET" && !!current?.is_central_kitchen);
  const isOutlet = hardScope === "OUTLET" || (!!current && !isCK);
  // Only Central Kitchen relevant
  const CK_ONLY = new Set([
    "/production", "/bom-builder", "/work-orders", "/warehouses",
    "/monthly-planning", "/production-period", "/production-digest", "/day-end-production",
    "/procurement", "/auto-po", "/purchases", "/purchase-receipts",
    "/vendor-payables", "/b2b-receivables", "/tax-invoices",
    "/prep-sheet", "/labels", "/batches", "/forecast",
    "/consumption-variance", "/outlet-menu-editor", "/menu-ranks",
    "/menu-master", "/menu-management", "/menu-pdf",
    "/traded-margin", "/loaded-cost", "/tax-templates",
    "/stock-entries", "/stock-ledger", "/stock-ageing",
    "/cycle-count", "/stock-value",
  ]);
  // Only Sellable Outlet relevant
  const OUTLET_ONLY = new Set([
    "/pos", "/live-shifts", "/all-orders", "/kds",
    "/86-board", "/handover-history",
    "/sales-reports", "/reports-hub", "/reports/deep-dive",
    "/reports/item-wise", "/outlet-day-end",
  ]);
  // Always show in both contexts
  const ALWAYS = new Set([
    "/", "/inventory", "/supply-chain", "/delivery-notes",
  ]);
  const passesContext = (to) => {
    if (!current) return true; // All outlets — full menu
    if (ALWAYS.has(to)) return true;
    if (isCK && OUTLET_ONLY.has(to)) return false;
    if (isOutlet && CK_ONLY.has(to)) return false;
    return true;
  };

  const roleCtxSections = SECTIONS
    .map((s) => ({
      ...s,
      links: s.links.filter((l) => l.roles.includes(role) && (!l.perm || can(l.perm)) && passesContext(l.to)),
    }))
    .filter((s) => s.links.length > 0);

  // Build the final section list:
  //  • "Advanced" items are always lifted into one collapsible group.
  //  • Simple Mode additionally keeps ONLY SIMPLE_PATHS in native sections and
  //    sends everything else to Advanced too.
  //  • Full Mode keeps everything native except the Advanced items.
  const visibleSections = React.useMemo(() => {
    const nativeSections = [];
    const advanced = [];
    roleCtxSections.forEach((s) => {
      const keep = s.links.filter((l) => {
        if (ADVANCED_PATHS.has(l.to)) return false;
        if (simpleMode) return SIMPLE_PATHS.has(l.to);
        return true;
      });
      const rest = s.links.filter((l) => !keep.includes(l));
      if (keep.length) nativeSections.push({ ...s, links: keep });
      advanced.push(...rest);
    });
    if (advanced.length) nativeSections.push({ title: "Advanced", links: advanced });
    return nativeSections;
  }, [simpleMode, roleCtxSections]);

  // Collapsible sections — remember state per user in localStorage.
  // Default: Home + the section containing the current route stay OPEN;
  // everything else starts COLLAPSED to reduce sidebar noise.
  const [collapsedSections, setCollapsedSections] = React.useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("ck-sidebar-collapsed-sections") || "null");
      if (saved) return saved;
    } catch (e) { /* fall through to default */ }
    const path = window.location.pathname;
    const state = {};
    SECTIONS.forEach((s) => {
      const hasActive = s.links.some((l) =>
        l.to === "/" ? path === "/" : path.startsWith(l.to)
      );
      state[s.title] = !(s.title === "Home" || hasActive);
    });
    return state;
  });
  const toggleSection = (title) => {
    setCollapsedSections((prev) => {
      const next = { ...prev, [title]: !prev[title] };
      localStorage.setItem("ck-sidebar-collapsed-sections", JSON.stringify(next));
      return next;
    });
  };

  const sidebarInner = (
    <>
      <div className="px-6 py-6 border-b border-[#E8E6E1]">
        <div className="ck-shine-loop flex items-center gap-3 rounded-lg p-1 -m-1">
          <img src="/logo.png" alt="SSV" className="w-11 h-11 rounded-full ring-2 ring-[#B87B41]/40" />
          <div>
            <div className="font-display font-black text-base leading-tight">Shree Siddhivinayak</div>
            <div className="font-display font-black text-base leading-tight text-[#7A2E2E]">Vadapav</div>
            <div className="small-label mt-0.5">Central Kitchen OS</div>
          </div>
        </div>
        <div className="mt-3 flex items-center rounded-full border border-[#E8E6E1] bg-white p-0.5 text-xs font-semibold" data-testid="simple-mode-toggle">
          <button
            onClick={() => simpleMode || toggleSimpleMode()}
            className={"flex-1 rounded-full px-3 py-1.5 transition-colors " + (simpleMode ? "bg-[#7A2E2E] text-white" : "text-[#8A8782]")}
            data-testid="simple-mode-on"
            title="Show only daily-use sections"
          >
            Simple
          </button>
          <button
            onClick={() => simpleMode && toggleSimpleMode()}
            className={"flex-1 rounded-full px-3 py-1.5 transition-colors " + (!simpleMode ? "bg-[#7A2E2E] text-white" : "text-[#8A8782]")}
            data-testid="simple-mode-off"
            title="Show every section"
          >
            Full
          </button>
        </div>
      </div>

      <nav className="py-3 flex-1 overflow-auto" data-testid="sidebar-nav">
        {visibleSections.map((section) => {
          const sectionKey = section.title;
          const sectionTestId = sectionKey.toLowerCase().replace(/[^a-z0-9]+/g, "-");
          const isCollapsed = collapsedSections[sectionKey] ?? (sectionKey === "Advanced");
          const activeInSection = section.links.some((l) =>
            l.to === "/" ? window.location.pathname === "/" : window.location.pathname.startsWith(l.to)
          );
          return (
            <div key={sectionKey} className="mb-1" data-testid={`section-${sectionTestId}`}>
              <button
                onClick={() => toggleSection(sectionKey)}
                className={
                  "group w-full flex items-center gap-2 px-6 pt-3 pb-1 text-[10px] font-bold uppercase tracking-[0.14em] " +
                  "hover:text-[#7A2E2E] transition-colors " +
                  (activeInSection ? "text-[#7A2E2E]" : "text-[#8A8782]")
                }
                data-testid={`section-toggle-${sectionTestId}`}
                aria-expanded={!isCollapsed}
              >
                <ChevronDown
                  size={11}
                  className={
                    "transition-transform duration-200 motion-reduce:transition-none " +
                    (isCollapsed ? "-rotate-90" : "rotate-0")
                  }
                />
                <span className="flex-1 text-left">{sectionKey}</span>
                <span
                  className={
                    "text-[9px] font-mono px-1.5 py-0.5 rounded-full border transition-colors " +
                    (activeInSection
                      ? "bg-[#F5E9D0] border-[#E8D4B5] text-[#7A2E2E]"
                      : "border-transparent text-[#8A8782] group-hover:border-[#EBE0CB]")
                  }
                >
                  {section.links.length}
                </span>
              </button>
              <div
                className={
                  "overflow-hidden transition-[max-height,opacity] duration-200 motion-reduce:transition-none " +
                  (isCollapsed ? "max-h-0 opacity-0" : "max-h-[2000px] opacity-100")
                }
              >
                {section.links.map((l) => {
                  const Icon = l.icon;
                  return (
                    <NavLink
                      key={l.to}
                      to={l.to}
                      end={l.to === "/"}
                      onClick={() => setMobileNavOpen(false)}
                      className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                      data-testid={l.testid}
                    >
                      <Icon size={16} />
                      <span>{l.label}</span>
                    </NavLink>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      <div className="border-t border-[#E8E6E1] p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center text-white font-display font-bold"
              style={{ background: "#F59E0B", color: "#0A0A0A" }}
            >
              {(user?.name || "U")[0].toUpperCase()}
            </div>
            <div className="text-sm">
              <div className="font-semibold leading-tight" data-testid="user-name">
                {user?.name}
              </div>
              <div className="text-xs text-[#8A8782] capitalize">{user?.role}</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle-btn"
              title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              className="p-2 rounded-lg border border-[#E8E6E1] hover:bg-[#F4F3EF] transition-colors"
            >
              {theme === "dark" ? <Sun size={15} className="text-[#B87B41]" /> : <Moon size={15} className="text-[#7A2E2E]" />}
            </button>
            <SyncStatusPill />
            <BackOfficeSyncPill />
            <NotificationBell testIdPrefix="sidebar-notif" />
          </div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={async () => {
            await logout();
            navigate("/login");
          }}
          className="w-full flex items-center justify-center gap-2 text-sm py-2 rounded-lg border border-[#E8E6E1] hover:bg-[#F4F3EF] transition-colors"
        >
          <LogOut size={14} /> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-[#F9F8F6] text-[#2C2A29]">
      {/* Desktop sidebar */}
      <aside
        className={`shrink-0 border-r border-[#E8E6E1] hidden lg:flex flex-col transition-all duration-200 ${sidebarCollapsed ? "w-14" : "w-64"}`}
        style={{ background: "var(--ck-sidebar)" }}
        data-testid="app-sidebar"
        data-collapsed={sidebarCollapsed ? "1" : "0"}
      >
        {sidebarCollapsed ? (
          <div className="flex flex-col items-center gap-2 py-3">
            <button
              onClick={toggleCollapse}
              className="p-2 rounded-lg hover:bg-[#E8E6E1]"
              title="Expand sidebar"
              data-testid="sidebar-expand"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        ) : (
          <>
            <button
              onClick={toggleCollapse}
              className="absolute lg:relative m-2 self-end p-1.5 rounded-lg hover:bg-[#E8E6E1] text-[#5C5A58]"
              title="Collapse sidebar"
              data-testid="sidebar-collapse"
            >
              <ChevronLeft size={16} />
            </button>
            {sidebarInner}
          </>
        )}
      </aside>

      {/* Mobile drawer */}
      {mobileNavOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-nav-drawer">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileNavOpen(false)}
            data-testid="mobile-nav-backdrop"
          />
          <aside
            className="absolute left-0 top-0 bottom-0 w-72 max-w-[85vw] flex flex-col shadow-2xl"
            style={{ background: "var(--ck-sidebar)" }}
          >
            <button
              className="absolute top-4 right-3 p-1.5 rounded-lg hover:bg-[#E8E6E1]"
              onClick={() => setMobileNavOpen(false)}
              data-testid="mobile-nav-close"
            >
              <X size={18} />
            </button>
            {sidebarInner}
          </aside>
        </div>
      )}

      <main className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top bar */}
        <div className="lg:hidden sticky top-0 z-40 px-3 py-2 border-b border-[#E8E6E1] bg-white flex flex-wrap items-center gap-2 min-w-0" data-testid="mobile-topbar">
          <button
            onClick={() => setMobileNavOpen(true)}
            className="p-2 rounded-lg border border-[#E8E6E1] hover:bg-[#F4F3EF]"
            data-testid="mobile-nav-toggle"
          >
            <Menu size={18} />
          </button>
          <img src="/logo.png" alt="SSV" className="w-8 h-8 rounded-full" />
          <div className="font-display font-black text-sm leading-tight flex-1 truncate">
            Shree Siddhivinayak <span className="text-[#7A2E2E]">Vadapav</span>
          </div>
          <SyncStatusPill />
          <BackOfficeSyncPill />
          <NotificationBell testIdPrefix="header-notif" />
        </div>

        {outlets.length > 0 && hardScope === "OUTLET" && (
          <div className="px-3 sm:px-6 py-2 border-b border-[#E8E6E1] bg-white flex items-center gap-2 sm:gap-3 flex-wrap" data-testid="outlet-topbar">
            <Store size={14} className="text-[#8A8782] hidden sm:block" />
            <span className="text-xs uppercase tracking-wider text-[#8A8782] hidden sm:inline">Viewing</span>
            {(["staff", "central_kitchen"].includes(role) && user?.outlet_id) ? (
              <div className="w-full sm:w-72 h-8 px-1 sm:px-3 flex items-center font-display font-bold text-sm text-[#2C2A29]" data-testid="outlet-selector-locked">
                {current ? (
                  <>
                    {current.is_central_kitchen ? "🏭 " : "🏪 "}{current.code} — {current.name}
                  </>
                ) : "—"}
                <span className="ml-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#F0E4DD] text-[#8A6240]">Locked</span>
              </div>
            ) : (
              <Select
                value={current?.id || "all"}
                onValueChange={(v) => {
                  if (v === "all") setCurrent(null);
                  else setCurrent(outlets.find((o) => o.id === v) || null);
                }}
              >
                <SelectTrigger className="w-full sm:w-72 h-8 border-0 font-display font-bold" data-testid="outlet-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent data-testid="outlet-selector-menu">
                  <SelectItem value="all" data-testid="outlet-option-all">All outlets (consolidated)</SelectItem>
                  {outlets.map((o) => (
                    <SelectItem key={o.id} value={o.id} data-testid={`outlet-option-${o.id}`}>
                      {o.is_central_kitchen ? "🏭 " : "🏪 "}{o.code} — {o.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {current && !(["staff", "central_kitchen"].includes(role) && user?.outlet_id) && (
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#F0E4DD] text-[#8A6240]">
                Filtered
              </span>
            )}
          </div>
        )}
        <div className="flex-1 min-h-0 min-w-0"><OperationalBoundary>{children}</OperationalBoundary></div>
      </main>
    </div>
  );
}
